"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Building2,
  Calendar,
  Clock,
  FileBarChart2,
  GraduationCap,
  Home,
  Mail,
  Scale,
  Sparkles,
  Stethoscope,
  Tag,
  User,
} from "lucide-react";
import { renderMarkdown } from "~/components/inbound-gpt/render-markdown";
import { DemoCTA, PrimaryCTA, TrishCTA } from "~/components/siteglue/siteglue-cta";
import { FounderInsight } from "~/components/siteglue/blog/founder-insight";
import { TRISH_AVATAR, TRISH_SIGNATURE } from "~/components/siteglue/roles/shared/constants";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import type { BlogPost } from "~/lib/inbound-gpt/blog";
import { AI_BUILT_FOR_HUMANS_LINE } from "~/lib/siteglue/ai-employee-plans";
import {
  estimateReadingMinutes,
  formatBlogDate,
  getPopularBlogPosts,
  getRecommendedEmployeesForPost,
  getRelatedBlogPosts,
  inferBlogTags,
  resolveBlogAuthor,
  SITEGLUE_BLOG_INDUSTRY_GUIDES,
  stripBlogArticleChrome,
} from "~/lib/siteglue/blog-article-meta";
import {
  SITEGLUE_FOUNDER_PHOTO,
  SITEGLUE_FOUNDER_PROFILE,
} from "~/lib/siteglue/blog-founder";
import { trackSiteGlueCta, trackSiteGlueEvent } from "~/lib/siteglue/cta-config";
import { SITEGLUE_COLORS, SITEGLUE_HITL_LINE } from "~/lib/siteglue/marketing-chrome";
import { cn } from "~/lib/utils";

const INDUSTRY_ICONS: Record<string, typeof Stethoscope> = {
  Dental: Stethoscope,
  Healthcare: Building2,
  Legal: Scale,
  Education: GraduationCap,
  "Home Services": Home,
};

function extractExcerpt(content: string, maxLen = 110): string {
  const plain = content
    .replace(/^#{1,6}\s+.*$/gm, "")
    .replace(/:::[\s\S]*?:::/g, " ")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\n+/g, " ")
    .trim();
  return plain.length > maxLen ? `${plain.slice(0, maxLen - 3)}...` : plain;
}

function SidebarCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm",
        className,
      )}
    >
      {children}
    </div>
  );
}

function BlogNewsletterCard({ location }: { location: "blog_sidebar" | "blog_footer" }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "done">("idle");

  return (
    <SidebarCard className="bg-gradient-to-br from-[#4f81bd]/[0.08] via-white to-[#9cbb5a]/[0.08]">
      <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
        <Mail className="h-3.5 w-3.5" aria-hidden />
        Newsletter
      </div>
      <h3 className="text-base font-semibold text-slate-900">Monthly AI Insights</h3>
      <p className="mt-1 text-sm leading-relaxed text-slate-600">
        Practical notes on AI Employees, Human-in-the-Loop™, and conversation-to-opportunity workflows.
      </p>
      {status === "done" ? (
        <p className="mt-4 text-sm font-medium text-[#4f81bd]" role="status">
          Thanks — you&apos;re on the list. Hire your first AI Employee anytime.
        </p>
      ) : (
        <form
          className="mt-4 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!email.trim()) return;
            trackSiteGlueEvent("blog_newsletter_signup", {
              cta_location: location,
              email_domain: email.split("@")[1] ?? "",
            });
            setStatus("done");
          }}
        >
          <label htmlFor={`blog-newsletter-${location}`} className="sr-only">
            Email address
          </label>
          <Input
            id={`blog-newsletter-${location}`}
            type="email"
            required
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-full border-slate-200 bg-white"
          />
          <Button
            type="submit"
            size="sm"
            className="w-full rounded-full text-white"
            style={{ backgroundColor: SITEGLUE_COLORS.blue }}
          >
            Subscribe
          </Button>
        </form>
      )}
    </SidebarCard>
  );
}

function BlogArticleSidebar({
  post,
  allPosts,
}: {
  post: BlogPost;
  allPosts: BlogPost[];
}) {
  const employees = useMemo(() => getRecommendedEmployeesForPost(post, 3), [post]);
  const popular = useMemo(() => getPopularBlogPosts(allPosts, post.id, 4), [allPosts, post.id]);

  return (
    <aside className="space-y-5" aria-label="SiteGlue resources">
      <SidebarCard className="border-[#4f81bd]/25 bg-gradient-to-br from-[#4f81bd]/[0.1] to-white">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
          {AI_BUILT_FOR_HUMANS_LINE}
        </p>
        <h2 className="mt-2 text-xl font-bold tracking-tight text-slate-900">
          Hire Your First AI Employee
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">{SITEGLUE_HITL_LINE}</p>
        <div className="mt-4">
          <PrimaryCTA
            location="blog_sidebar"
            fullWidth
            showArrow
            testId="blog-sidebar-primary-cta"
          />
        </div>
      </SidebarCard>

      <SidebarCard>
        <div className="flex items-start gap-3">
          <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
            <Image src={TRISH_AVATAR} alt="Trish" fill className="object-cover" sizes="56px" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#9cbb5a]">
              Meet Trish
            </p>
            <h3 className="text-base font-semibold text-slate-900">{TRISH_SIGNATURE.agentName}</h3>
            <p className="text-sm text-slate-500">{TRISH_SIGNATURE.agentTitle}</p>
          </div>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          Trish helps you identify the right AI Employee, configure your channels and scheduling, and
          generate a personalized AI Opportunity Report.
        </p>
        <div className="mt-4">
          <TrishCTA
            location="blog_sidebar"
            fullWidth
            testId="blog-sidebar-trish-cta"
          />
        </div>
      </SidebarCard>

      <SidebarCard>
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
          <FileBarChart2 className="h-3.5 w-3.5 text-[#4f81bd]" aria-hidden />
          AI Opportunity Report
        </div>
        <h3 className="text-base font-semibold text-slate-900">See where AI can help first</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          Trish prepares a tailored report on channels, scheduling fit, Human-in-the-Loop™ strategy,
          and the best AI Employee plan to start with.
        </p>
        <div className="mt-4">
          <TrishCTA
            location="blog_sidebar"
            variant="text"
            size="sm"
            className="text-sm font-semibold"
            testId="blog-sidebar-report-cta"
          />
        </div>
      </SidebarCard>

      <SidebarCard>
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
          <Bot className="h-3.5 w-3.5 text-[#4f81bd]" aria-hidden />
          Recommended AI Employees
        </div>
        <ul className="space-y-3">
          {employees.map((emp) => (
            <li key={emp.href}>
              <Link
                href={emp.href}
                className="group block rounded-xl border border-slate-100 bg-slate-50/80 p-3 transition hover:border-[#4f81bd]/30 hover:bg-white"
                onClick={() => {
                  trackSiteGlueCta({
                    action: "exploreLiveEmployees",
                    label: emp.title,
                    location: "blog_employee_card",
                    destination: emp.href,
                    extra: { agent_name: emp.title },
                  });
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-900 group-hover:text-[#4f81bd]">
                    {emp.title}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-[#4f81bd]" />
                </div>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{emp.description}</p>
              </Link>
            </li>
          ))}
        </ul>
      </SidebarCard>

      <SidebarCard>
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
          <Sparkles className="h-3.5 w-3.5 text-[#4f81bd]" aria-hidden />
          Popular Resources
        </div>
        <ul className="space-y-2.5">
          {popular.map((item) => (
            <li key={item.id}>
              <Link
                href={`/blog/${item.slug}`}
                className="text-sm font-medium text-slate-800 transition hover:text-[#4f81bd]"
                onClick={() => {
                  trackSiteGlueEvent("blog_related_article_clicked", {
                    source: "sidebar_popular",
                    destination_slug: item.slug ?? "",
                  });
                }}
              >
                {item.title}
              </Link>
            </li>
          ))}
        </ul>
        <div className="mt-4 border-t border-slate-100 pt-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
            Explore
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            {[
              { label: "Platform", href: "/platform" },
              { label: "Features", href: "/features" },
              { label: "Pricing", href: "/pricing" },
              { label: "AI Employees", href: "/agents" },
              { label: "Human-in-the-Loop™", href: "/features#human-in-the-loop" },
            ].map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-600 hover:bg-[#4f81bd]/10 hover:text-[#4f81bd]"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </SidebarCard>

      <SidebarCard>
        <div className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
          Industry Guides
        </div>
        <ul className="space-y-2">
          {SITEGLUE_BLOG_INDUSTRY_GUIDES.map((guide) => {
            const Icon = INDUSTRY_ICONS[guide.label] ?? Building2;
            return (
              <li key={guide.href}>
                <Link
                  href={guide.href}
                  className="flex items-center gap-2 rounded-lg px-1 py-1.5 text-sm font-medium text-slate-700 transition hover:text-[#4f81bd]"
                  onClick={() => {
                    trackSiteGlueEvent("blog_industry_guide_clicked", {
                      industry: guide.label,
                      destination: guide.href,
                    });
                  }}
                >
                  <Icon className="h-4 w-4 text-[#4f81bd]" aria-hidden />
                  {guide.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </SidebarCard>

      <BlogNewsletterCard location="blog_sidebar" />
    </aside>
  );
}

export function SiteGlueBlogArticle({
  post,
  allPosts,
}: {
  post: BlogPost;
  allPosts: BlogPost[];
}) {
  const completionSentinelRef = useRef<HTMLElement>(null);
  const completionSent = useRef(false);
  const author = useMemo(() => resolveBlogAuthor(post), [post]);
  const tags = useMemo(() => inferBlogTags(post), [post]);
  const readingMinutes = useMemo(() => estimateReadingMinutes(post.content), [post.content]);
  const body = useMemo(() => stripBlogArticleChrome(post.content), [post.content]);
  const related = useMemo(() => getRelatedBlogPosts(post, allPosts, 3), [post, allPosts]);
  const employees = useMemo(() => getRecommendedEmployeesForPost(post, 3), [post]);

  useEffect(() => {
    completionSent.current = false;
    const el = completionSentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || completionSent.current) return;
        completionSent.current = true;
        trackSiteGlueEvent("blog_article_completed", {
          article_slug: post.slug ?? String(post.id),
          article_title: post.title,
        });
      },
      { threshold: 0.4, rootMargin: "0px 0px -10% 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [post.id, post.slug, post.title]);

  return (
    <div className="relative min-h-screen bg-[#fafbfc] pb-20">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-72 opacity-70"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 20% 0%, rgba(79,129,189,0.14), transparent 55%), radial-gradient(ellipse 50% 40% at 90% 10%, rgba(156,187,90,0.1), transparent 50%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 pb-8 pt-8 sm:px-6 lg:px-8">
        <Link
          href="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 transition hover:text-slate-900"
          data-testid="button-back-to-blog"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to Blog
        </Link>

        <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(280px,340px)] lg:items-start lg:gap-12">
          <div>
            <header className="mb-10 border-b border-slate-200/80 pb-8">
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
                <span>Resource Center</span>
                <span className="text-slate-300" aria-hidden>
                  ·
                </span>
                <span className="text-slate-500">Blog</span>
              </div>
              <h1
                className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-[2.5rem] lg:leading-tight"
                data-testid="heading-blog-post"
              >
                {post.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4" aria-hidden />
                  {readingMinutes} min read
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" aria-hidden />
                  Published {formatBlogDate(post.createdAt)}
                </span>
              </div>
              <div className="mt-5 flex items-center gap-3">
                {author.name === SITEGLUE_FOUNDER_PROFILE.name ? (
                  <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-full border border-slate-200 bg-slate-50">
                    <Image
                      src={SITEGLUE_FOUNDER_PHOTO}
                      alt={author.name}
                      fill
                      className="object-cover object-[center_18%]"
                      sizes="44px"
                    />
                  </div>
                ) : (
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#4f81bd]/10 text-[#4f81bd]">
                    <User className="h-5 w-5" aria-hidden />
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-slate-900">{author.name}</p>
                  <p className="text-sm text-slate-500">{author.role}</p>
                </div>
              </div>
              {tags.length > 0 ? (
                <ul className="mt-5 flex flex-wrap gap-2" aria-label="Article tags">
                  {tags.map((tag) => (
                    <li
                      key={tag}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600"
                    >
                      <Tag className="h-3 w-3 text-[#4f81bd]" aria-hidden />
                      {tag}
                    </li>
                  ))}
                </ul>
              ) : null}
            </header>

            <FounderInsight insight={post.founderInsight} className="mb-10" />

            <article
              className="prose prose-lg max-w-none prose-headings:scroll-mt-28 prose-a:text-[#4f81bd]"
              data-testid="blog-post-content"
            >
              {renderMarkdown(body)}
            </article>

            <div className="mt-12 lg:hidden">
              <BlogArticleSidebar post={post} allPosts={allPosts} />
            </div>

            <section
              ref={completionSentinelRef}
              className="mt-14 space-y-10 border-t border-slate-200 pt-12"
              aria-label="Next steps"
            >
              <div>
                <h2 className="text-xl font-bold text-slate-900">Recommended AI Employees</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Roles that pair well with this article — hire one, then expand.
                </p>
                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  {employees.map((emp) => (
                    <Link
                      key={emp.href}
                      href={emp.href}
                      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-[#4f81bd]/35 hover:shadow-md"
                      onClick={() => {
                        trackSiteGlueCta({
                          action: "exploreLiveEmployees",
                          label: emp.title,
                          location: "blog_footer",
                          destination: emp.href,
                          extra: { agent_name: emp.title },
                        });
                      }}
                    >
                      <p className="font-semibold text-slate-900">{emp.title}</p>
                      <p className="mt-1 text-sm text-slate-500">{emp.description}</p>
                    </Link>
                  ))}
                </div>
              </div>

              {related.length > 0 ? (
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Related Articles</h2>
                  <ul className="mt-4 space-y-3">
                    {related.map((item) => (
                      <li key={item.id}>
                        <Link
                          href={`/blog/${item.slug}`}
                          className="group block rounded-xl border border-slate-200 bg-white px-4 py-3 transition hover:border-[#4f81bd]/30"
                          onClick={() => {
                            trackSiteGlueEvent("blog_related_article_clicked", {
                              source: "article_footer",
                              destination_slug: item.slug ?? "",
                            });
                          }}
                        >
                          <p className="font-medium text-slate-900 group-hover:text-[#4f81bd]">
                            {item.title}
                          </p>
                          <p className="mt-1 text-sm text-slate-500">
                            {extractExcerpt(item.content)}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div>
                <h2 className="text-xl font-bold text-slate-900">Industry Guides</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {SITEGLUE_BLOG_INDUSTRY_GUIDES.map((guide) => (
                    <Link
                      key={guide.href}
                      href={guide.href}
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-[#4f81bd]/40 hover:text-[#4f81bd]"
                    >
                      {guide.label}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="overflow-hidden rounded-3xl border border-[#4f81bd]/20 bg-gradient-to-br from-[#4f81bd]/[0.12] via-white to-[#9cbb5a]/[0.1] p-8 text-center shadow-sm sm:p-10">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
                  {AI_BUILT_FOR_HUMANS_LINE}
                </p>
                <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  Ready to Hire Your First AI Employee?
                </h2>
                <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
                  {SITEGLUE_HITL_LINE} Start free with Intern, then expand channels as you grow.
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  <PrimaryCTA
                    location="blog_footer"
                    showArrow
                    testId="blog-final-signup"
                  />
                  <DemoCTA location="blog_footer" testId="blog-final-demo" />
                </div>
              </div>

            </section>
          </div>

          <div className="hidden lg:sticky lg:top-24 lg:block lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pb-8">
            <BlogArticleSidebar post={post} allPosts={allPosts} />
          </div>
        </div>
      </div>
    </div>
  );
}
